import type { BeatSlot, NoteFigure } from '../types';
import {
  applyFigureToBeat,
  cellCountForFigure,
  createEmptyMeasure,
  createEmptyPattern,
  resizePattern,
} from './pattern';

export interface RhythmCell {
  figure: NoteFigure;
  hit: boolean;
}

export interface RhythmMeasure {
  slots: (Omit<BeatSlot, 'cells'> & { cells: RhythmCell[] })[];
}

export interface RhythmPattern {
  beatsPerMeasure: number;
  measures: RhythmMeasure[];
}

export function figureForRhythmCell(
  slotFigure: NoteFigure,
  _cellIndex: number,
  cellCount: number,
): NoteFigure {
  if (slotFigure === 'corchea' || slotFigure === 'semicorchea' || slotFigure === 'negra') {
    return slotFigure;
  }
  if (slotFigure === 'blanca') {
    return cellCount === 1 ? 'blanca' : 'negra';
  }
  if (slotFigure === 'redonda') {
    return cellCount === 1 ? 'redonda' : 'negra';
  }
  return 'negra';
}

function buildRhythmCells(
  slot: Pick<BeatSlot, 'figure' | 'span'>,
  prev: RhythmCell[] = [],
): RhythmCell[] {
  const count = cellCountForFigure(slot.figure, slot.span);
  return Array.from({ length: count }, (_, ci) => ({
    figure: figureForRhythmCell(slot.figure, ci, count),
    hit: prev[ci]?.hit ?? false,
  }));
}

export function createEmptyRhythmPattern(
  measureCount: number,
  beatsPerMeasure: number,
): RhythmPattern {
  const base = createEmptyPattern(measureCount, beatsPerMeasure);
  return {
    beatsPerMeasure,
    measures: base.measures.map((m) => ({
      slots: m.slots.map((s) => ({
        ...s,
        cells: buildRhythmCells(s),
      })),
    })),
  };
}

export function resizeRhythmPattern(
  pattern: RhythmPattern,
  measureCount: number,
  beatsPerMeasure: number,
): RhythmPattern {
  const melodyStub = createEmptyPattern(pattern.measures.length, pattern.beatsPerMeasure);
  melodyStub.measures = pattern.measures.map((m) => ({
    slots: m.slots.map((s) => ({
      ...s,
      cells: s.cells.map(() => ({ note: 'rest' as const })),
    })),
  }));

  const resized = resizePattern(melodyStub, measureCount, beatsPerMeasure);

  return {
    beatsPerMeasure,
    measures: resized.measures.map((m, mi) => {
      const prev = pattern.measures[mi];
      return {
        slots: m.slots.map((s, si) => {
          const prevSlot = prev?.slots[si];
          const cells =
            prevSlot && prevSlot.cells.length === s.cells.length
              ? prevSlot.cells.map((c) => ({ ...c }))
              : buildRhythmCells(s, prevSlot?.cells);
          return { ...s, cells };
        }),
      };
    }),
  };
}

export function applyRhythmFigureToBeat(
  measure: RhythmMeasure,
  beatIndex: number,
  figure: NoteFigure,
  beatsPerMeasure: number,
): RhythmMeasure {
  const prevMeasure = measure;
  const melodyMeasure = {
    slots: measure.slots.map((s) => ({
      ...s,
      cells: s.cells.map(() => ({ note: 'rest' as const })),
    })),
  };

  const updated = applyFigureToBeat(melodyMeasure, beatIndex, figure, beatsPerMeasure);

  return {
    slots: updated.slots.map((s, i) => {
      if (!s.active) return { ...s, cells: [] as RhythmCell[] };
      const prevSlot = prevMeasure.slots[i];
      const count = s.cells.length;

      if (prevSlot && prevSlot.cells.length === count && i < beatIndex) {
        return { ...s, cells: prevSlot.cells.map((c) => ({ ...c })) };
      }

      const hitSource =
        i >= beatIndex ? prevMeasure.slots[beatIndex]?.cells : prevSlot?.cells;

      const cells: RhythmCell[] = Array.from({ length: count }, (_, ci) => ({
        figure: figureForRhythmCell(s.figure, ci, count),
        hit: hitSource?.[ci]?.hit ?? false,
      }));
      return { ...s, cells };
    }),
  };
}

export function createEmptyRhythmMeasure(beatsPerMeasure: number): RhythmMeasure {
  const m = createEmptyMeasure(beatsPerMeasure);
  return {
    slots: m.slots.map((s) => ({ ...s, cells: buildRhythmCells(s) })),
  };
}
